import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { getSystemAIPrompts, createSystemAIPrompt, updateSystemAIPrompt, deleteSystemAIPrompt } from '../services/aiPromptService';
import { logger } from '../utils/logger';
import './AIPrompts.css';

const AIPrompts = forwardRef((props, ref) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Prompt',
    body: '',
    directions: '',
    updatable_fields: [],
    context_data: []
  });
  
  // Available feature fields that can be updated
  const UPDATABLE_FIELDS = [
    { value: 'name', label: 'Name' },
    { value: 'description', label: 'Description' },
    { value: 'details', label: 'Details' },
    { value: 'ai_prompt', label: 'AI Prompt' }
  ];

  // Available context data options
  const CONTEXT_OPTIONS = [
    'Full hierarchy feature name, description, details',
    'Persona tag definitions and data',
    'Feature Map description'
  ];

  // Product Details document types (available for all feature groups)
  const PRODUCT_DETAILS_TYPES = [
    'Product Overview',
    'Technical Specs',
    'User Guide',
    'API Documentation',
    'Glossary'
  ];

  const PRODUCT_DETAILS_PREFIX = 'Product Details: ';
  const originalFormDataRef = useRef(null);

  useEffect(() => {
    loadPrompts();
  }, []);

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!showEditDialog || !originalFormDataRef.current) {
      return false;
    }
    const original = originalFormDataRef.current;
    const fieldsChanged = (
      formData.name !== original.name ||
      formData.type !== original.type ||
      formData.body !== original.body ||
      formData.directions !== original.directions
    );
    // Check if updatable_fields array changed
    const fieldsArrayChanged = JSON.stringify(formData.updatable_fields?.sort() || []) !== 
                               JSON.stringify(original.updatable_fields?.sort() || []);
    // Check if context_data array changed
    const contextDataChanged = JSON.stringify(formData.context_data?.sort() || []) !== 
                               JSON.stringify(original.context_data?.sort() || []);
    return fieldsChanged || fieldsArrayChanged || contextDataChanged;
  };

  // Expose hasUnsavedChanges to parent component via ref
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: hasUnsavedChanges
  }));

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!showEditDialog || !originalFormDataRef.current) {
        return;
      }
      const original = originalFormDataRef.current;
      const hasChanges = (
        formData.name !== original.name ||
        formData.type !== original.type ||
        formData.body !== original.body ||
        formData.directions !== original.directions
      );
      
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    if (showEditDialog) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [showEditDialog, formData]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSystemAIPrompts();
      setPrompts(data);
    } catch (err) {
      setError(err.message || 'Failed to load system AI prompts');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    // Check for unsaved changes in current dialog
    if (showEditDialog && hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them and create a new prompt?')) {
        return;
      }
    }
    
    setEditingPrompt(null);
    const newFormData = {
      name: '',
      type: 'Prompt',
      body: '',
      directions: '',
      updatable_fields: [],
      context_data: []
    };
    setFormData(newFormData);
    originalFormDataRef.current = { ...newFormData };
    setShowEditDialog(true);
  };

  const handleEdit = (prompt) => {
    // Check for unsaved changes in current dialog
    if (showEditDialog && hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them and edit another prompt?')) {
        return;
      }
    }
    
    let contextData = prompt.context_data || [];
    
    // Migrate legacy "Product Details" to all document types (for system prompts, we include all types)
    if (contextData.includes('Product Details')) {
      // Remove legacy "Product Details"
      contextData = contextData.filter(item => item !== 'Product Details');
      
      // Add all product detail document types
      PRODUCT_DETAILS_TYPES.forEach(docType => {
        const optionValue = `${PRODUCT_DETAILS_PREFIX}${docType}`;
        if (!contextData.includes(optionValue)) {
          contextData.push(optionValue);
        }
      });
    }
    
    setEditingPrompt(prompt);
    const newFormData = {
      name: prompt.name || '',
      type: prompt.type || 'Prompt',
      body: prompt.body || '',
      directions: prompt.directions || '',
      updatable_fields: prompt.updatable_fields || [],
      context_data: contextData
    };
    setFormData(newFormData);
    originalFormDataRef.current = { ...newFormData };
    setShowEditDialog(true);
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this system AI prompt?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteSystemAIPrompt(promptId);
      await loadPrompts();
    } catch (err) {
      setError(err.message || 'Failed to delete system AI prompt');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Prompt name is required');
      return;
    }

    if (!formData.body.trim()) {
      setError('Body is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (editingPrompt) {
        await updateSystemAIPrompt(editingPrompt.id, formData);
      } else {
        await createSystemAIPrompt(formData);
      }
      
      setShowEditDialog(false);
      originalFormDataRef.current = null;
      await loadPrompts();
    } catch (err) {
      setError(err.message || `Failed to ${editingPrompt ? 'update' : 'create'} system AI prompt`);
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
        return;
      }
    }
    
    setShowEditDialog(false);
    setEditingPrompt(null);
    setFormData({
      name: '',
      type: 'Prompt',
      body: '',
      directions: '',
      updatable_fields: [],
      context_data: []
    });
    originalFormDataRef.current = null;
    setError(null);
  };

  const handleCopyId = async (promptId, event) => {
    event.stopPropagation();
    const button = event.currentTarget;
    if (!button) {
      logger.error('Button element not found');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(promptId);
      // Show temporary feedback
      const icon = button.querySelector('i');
      if (icon) {
        const originalClass = icon.className;
        icon.className = 'fas fa-check';
        button.style.color = '#4CAF50';
        setTimeout(() => {
          icon.className = originalClass;
          button.style.color = '';
        }, 2000);
      }
    } catch (err) {
      logger.error('Failed to copy ID:', err);
      alert('Failed to copy ID to clipboard');
    }
  };

  return (
    <div className="ai-prompts-container">
      <div className="ai-prompts-header">
        <h3>System AI</h3>
        <button
          type="button"
          className="add-ai-prompt-button"
          onClick={handleCreate}
          disabled={loading}
        >
          <i className="fas fa-plus"></i> Add System AI Prompt
        </button>
      </div>

      {error && !showEditDialog && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {loading && !showEditDialog && prompts.length === 0 ? (
        <div className="loading">Loading system AI prompts...</div>
      ) : prompts.length === 0 ? (
        <div className="no-ai-prompts">
          No system AI prompts yet. Click "Add System AI Prompt" to create one.
        </div>
      ) : (
        <div className="ai-prompts-list">
          {prompts.map((prompt) => (
            <div key={prompt.id} className="ai-prompt-item">
              <div className="ai-prompt-header">
                <div className="ai-prompt-title-section">
                  <h4>{prompt.name}</h4>
                  <span className={`ai-prompt-type-pill type-${(prompt.type || 'Prompt').toLowerCase()}`}>
                    {prompt.type || 'Prompt'}
                  </span>
                  <div className="ai-prompt-id-section">
                    <span className="ai-prompt-id-label">ID:</span>
                    <span className="ai-prompt-id-value">{prompt.id}</span>
                    <button
                      type="button"
                      className="copy-id-button"
                      onClick={(e) => handleCopyId(prompt.id, e)}
                      title="Copy ID to clipboard"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
                <div className="ai-prompt-actions">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => handleEdit(prompt)}
                    title="Edit system AI prompt"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="icon-button delete-button"
                    onClick={() => handleDelete(prompt.id)}
                    title="Delete system AI prompt"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div className="ai-prompt-preview">
                <div className="ai-prompt-preview-section">
                  <strong>Body:</strong>
                  <div className="ai-prompt-preview-text">
                    {prompt.body.length > 200
                      ? prompt.body.substring(0, 200) + '...'
                      : prompt.body}
                  </div>
                </div>
                {prompt.directions && (
                  <div className="ai-prompt-preview-section">
                    <strong>Directions:</strong>
                    <div className="ai-prompt-preview-text">
                      {prompt.directions.length > 200
                        ? prompt.directions.substring(0, 200) + '...'
                        : prompt.directions}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditDialog && (
        <div className="edit-dialog-overlay" onClick={handleCancel}>
          <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="edit-dialog-header">
              <h3>{editingPrompt ? 'Edit System AI Prompt' : 'Create System AI Prompt'}</h3>
              <button
                type="button"
                className="close-button"
                onClick={handleCancel}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <form onSubmit={handleSave} className="edit-dialog-form">
              <div className="form-group">
                <label htmlFor="ai-prompt-name">
                  Name <span className="required">*</span>
                </label>
                <input
                  id="ai-prompt-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter prompt name"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="ai-prompt-type">
                  Type <span className="required">*</span>
                </label>
                <select
                  id="ai-prompt-type"
                  value={formData.type}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value });
                    setError(null);
                  }}
                  disabled={loading}
                  required
                >
                  <option value="Context">Context</option>
                  <option value="Prompt">Prompt</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ai-prompt-body">
                  Body <span className="required">*</span>
                </label>
                <textarea
                  id="ai-prompt-body"
                  value={formData.body}
                  onChange={(e) => {
                    setFormData({ ...formData, body: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter prompt body"
                  disabled={loading}
                  required
                  rows={10}
                  className="ai-prompt-textarea"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ai-prompt-directions">
                  Directions
                </label>
                <textarea
                  id="ai-prompt-directions"
                  value={formData.directions}
                  onChange={(e) => {
                    setFormData({ ...formData, directions: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter directions (optional)"
                  disabled={loading}
                  rows={6}
                  className="ai-prompt-textarea"
                />
              </div>

              <div className="form-group">
                <label>
                  Updatable Fields
                </label>
                <div className="updatable-fields-container">
                  <small className="help-text">
                    Select which feature fields can be updated from the AI response
                  </small>
                  <div className="updatable-fields-checkboxes">
                    {UPDATABLE_FIELDS.map((field) => (
                      <label key={field.value} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.updatable_fields?.includes(field.value) || false}
                          onChange={(e) => {
                            const currentFields = formData.updatable_fields || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                updatable_fields: [...currentFields, field.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                updatable_fields: currentFields.filter(f => f !== field.value)
                              });
                            }
                            setError(null);
                          }}
                          disabled={loading}
                        />
                        <span>{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Context Data
                </label>
                <div className="updatable-fields-container">
                  <small className="help-text">
                    Select which context data to include in the prompt
                  </small>
                  <div className="updatable-fields-checkboxes">
                    {CONTEXT_OPTIONS.map((option) => (
                      <label key={option} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.context_data?.includes(option) || false}
                          onChange={(e) => {
                            const currentContext = formData.context_data || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                context_data: [...currentContext, option]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                context_data: currentContext.filter(c => c !== option)
                              });
                            }
                            setError(null);
                          }}
                          disabled={loading}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                    <div className="product-details-options">
                      <div className="product-details-label">Product Details:</div>
                      {PRODUCT_DETAILS_TYPES.map((docType) => {
                        const optionValue = `${PRODUCT_DETAILS_PREFIX}${docType}`;
                        return (
                          <label key={docType} className="checkbox-label product-detail-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.context_data?.includes(optionValue) || false}
                              onChange={(e) => {
                                const currentContext = formData.context_data || [];
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    context_data: [...currentContext, optionValue]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    context_data: currentContext.filter(c => c !== optionValue)
                                  });
                                }
                                setError(null);
                              }}
                              disabled={loading}
                            />
                            <span>{docType}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="edit-dialog-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
});

AIPrompts.displayName = 'AIPrompts';

export default AIPrompts;

